import { useRouter } from 'next/router';
import Cookie from 'js-cookie';
import { Link, LinkWithIcon } from '@zoopla-eng/zds-components/link';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { drawerListItemStyle, navButtonStyle } from './Header.styles.css';
import {
    EditMedium,
    EmailMedium,
    LogoutMedium,
    MySituationMedium,
    UserMedium
} from '@zoopla-eng/zds-components/icons';
import { Dialog } from '@zoopla-eng/zds-components/dialog';
import {
    drawerListStyle,
    styledMobileIcon,
    styledDesktopLabel,
    buttonContainerStyles,
    accountMenuBackgroundStyles,
    accountMenuStyles,
    styledMobileLabel
} from './AccountMenu.css';
import {
    trackMenuClose,
    trackMenuItem,
    trackMenuShown
} from './AccountMenu.tracking';
import { getSegmentPage } from './helpers/getSegmentPage';

export const linksList = [
    {
        name: 'Account',
        url: '/my-zoopla/',
        trackingLabel: 'account'
    },
    {
        name: 'Saved listings',
        url: '/favourites/properties/',
        trackingLabel: 'saved_listings'
    },
    {
        name: 'Saved searches',
        url: '/myaccount/alerts-searches/',
        trackingLabel: 'saved_searched'
    },
    {
        name: 'My Home',
        url: '/my-home/',
        trackingLabel: 'my_home'
    },
    {
        name: 'Tracked properties',
        url: '/favourites/tracked-properties/',
        trackingLabel: 'tracked_properties'
    }
];

export const accountSettingsLinks = [
    {
        text: 'Profile',
        href: '/myaccount/profile/',
        icon: UserMedium,
        trackingLabel: 'profile'
    },
    {
        text: 'My situation',
        href: '/myaccount/my-situation/',
        icon: MySituationMedium,
        trackingLabel: 'my_situation'
    },
    {
        text: 'Email preferences',
        href: '/myaccount/account/preferences/',
        icon: EmailMedium,
        trackingLabel: 'email_preferences'
    },
    {
        text: 'Change email',
        href: '/myaccount/account/email/',
        icon: EditMedium,
        trackingLabel: 'change_email'
    },
    {
        text: 'Change password',
        href: '/myaccount/account/password/',
        icon: EditMedium,
        trackingLabel: 'change_password'
    },
    {
        text: 'Sign out',
        href: '/?signout=1',
        icon: LogoutMedium,
        trackingLabel: 'sign_out'
    }
];

export const AccountMenu = () => {
    const router = useRouter();

    const segmentPage = getSegmentPage(router.pathname);

    const onOpenChange = (isOpen: boolean) => {
        if (isOpen) {
            trackMenuShown(segmentPage);
        } else {
            trackMenuClose(segmentPage);
        }
    };

    const removeLogoutCookie = () => Cookie.remove('adb_he');

    return (
        <Dialog
            trigger={
                <button
                    className={navButtonStyle}
                    aria-label="Open Account Menu"
                    data-testid="account-mobile-hamburger"
                >
                    <div className={`${buttonContainerStyles}`}>
                        <span className={styledMobileIcon}>
                            <UserMedium accessibilityType="decorative" />
                        </span>
                        <span
                            className={`${styledMobileLabel} ${fontGroups.zester}`}
                        >
                            Account
                        </span>
                        <span
                            className={`${styledDesktopLabel} ${fontGroups.ladle}`}
                        >
                            Account
                        </span>
                    </div>
                </button>
            }
            label="Account menu"
            onOpenChange={onOpenChange}
            variant="sideSheetRight"
        >
            <nav aria-label="Account menu">
                <ul
                    className={`${accountMenuStyles} ${fontGroups.mug} ${accountMenuBackgroundStyles}`}
                >
                    {linksList.map(({ name, url, trackingLabel }) => (
                        <li className={drawerListItemStyle} key={url}>
                            <Link
                                href={url}
                                muted
                                disableVisited
                                onClick={() => {
                                    trackMenuItem(trackingLabel, segmentPage);
                                }}
                            >
                                <span>{name}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
                <div
                    className={fontGroups.ladle}
                    role="navigation"
                    aria-label="Account settings"
                >
                    <ul className={`${drawerListStyle}`}>
                        {accountSettingsLinks.map(
                            ({ text, href, icon: LinkIcon, trackingLabel }) => (
                                <li key={href}>
                                    <div className={`${fontGroups.ladle}`}>
                                        <LinkWithIcon
                                            href={href}
                                            onClick={() => {
                                                trackMenuItem(
                                                    trackingLabel,
                                                    segmentPage
                                                );
                                                if (text === 'Sign out') {
                                                    removeLogoutCookie();
                                                }
                                            }}
                                            iconPrefix={
                                                <LinkIcon accessibilityType="decorative" />
                                            }
                                            muted
                                            withIcon
                                            disableVisited
                                        >
                                            {text}
                                        </LinkWithIcon>
                                    </div>
                                </li>
                            )
                        )}
                    </ul>
                </div>
            </nav>
        </Dialog>
    );
};
